/**
 * The header's actions (docs/ux/matter-hub.md): one ink primary, secondaries
 * for the other forward and exceptional events whether allowed or refused,
 * and More for Cancel and a refused park. Every unit carries its engine
 * line: "to" plus the target chip, or "Refused:" plus the engine's reason
 * verbatim, as visible text the projector audience can read. Refused units
 * stay focusable (aria-disabled) and never open anything.
 */

import { EVENT_DISPLAY, eventLabel, statusLabel, type Status, type StatusEvent } from '@beam/core/status';
import { cn } from 'cn';
import { BanIcon, ChevronDownIcon } from 'lucide-react';
import { StatusChip } from '@/components/matter/StatusChip';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import type { TransitionOption } from '@/app/types';
import { layoutActions } from './actions';

export type ActionClusterProps = {
  matterRef: string;
  status: Status;
  options: TransitionOption[];
  parkedFrom: Status | null;
  offline: boolean;
  busyEvent: StatusEvent['kind'] | null;
  onEvent: (option: TransitionOption) => void;
  /** Compact: 32 px buttons for the sticky strip. */
  size?: 'default' | 'compact';
  className?: string;
};

export function EngineLine({ option, parkedFrom, id, className }: { option: TransitionOption; parkedFrom: Status | null; id: string; className?: string }) {
  if (option.allowed && option.to !== undefined) {
    return (
      <span id={id} className={cn('flex items-center gap-1 text-xs text-beam-text-muted', className)}>
        to <StatusChip status={option.to} size="sm" />
        {option.kind === 'resume' && parkedFrom !== null ? <span>(parked from)</span> : null}
      </span>
    );
  }
  return (
    <span id={id} className={cn('block max-w-[280px] text-xs leading-snug text-beam-text-muted', className)}>
      Refused: {option.reason}
    </span>
  );
}

export function ActionUnit({ option, primary, parkedFrom, offline, busyEvent, onEvent, size = 'default' }: {
  option: TransitionOption;
  primary: boolean;
  parkedFrom: Status | null;
  offline: boolean;
  /** The event in flight, if any: every unit is disabled while one moves, only the moving one shows its busy label. */
  busyEvent: StatusEvent['kind'] | null;
  onEvent: (o: TransitionOption) => void;
  size?: 'default' | 'compact';
}) {
  const lineId = `engine-${option.kind}`;
  const refused = !option.allowed;
  const busy = busyEvent === option.kind;
  return (
    <div data-slot="action-unit" data-event={option.kind} className="flex flex-col gap-1">
      <Button
        variant={primary ? 'default' : 'outline'}
        size={size === 'compact' ? 'default' : 'lg'}
        data-primary={primary ? 'true' : undefined}
        aria-disabled={refused ? 'true' : undefined}
        aria-describedby={size === 'compact' ? undefined : lineId}
        disabled={offline || busyEvent !== null}
        title={offline ? 'Actions are paused while offline' : undefined}
        onClick={() => {
          if (refused) return;
          onEvent(option);
        }}
        className={cn('h-11 md:h-9 justify-start', size === 'compact' && 'h-8 md:h-8', refused && 'cursor-default text-beam-text-muted hover:bg-beam-paper')}
      >
        {busy ? busyLabel(option.kind) : eventLabel(option.kind)}
      </Button>
      {size === 'compact' ? null : <EngineLine option={option} parkedFrom={parkedFrom} id={lineId} />}
    </div>
  );
}

const busyLabel = (kind: StatusEvent['kind']): string => EVENT_DISPLAY[kind].busy;

export function MoreMenu({ items, parkedFrom, offline, busy = false, onEvent, size = 'default' }: { items: TransitionOption[]; parkedFrom: Status | null; offline: boolean; busy?: boolean; onEvent: (o: TransitionOption) => void; size?: 'default' | 'compact' }) {
  if (items.length === 0) return null;
  const park = items.filter((o) => o.kind !== 'cancel');
  const cancel = items.find((o) => o.kind === 'cancel');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size={size === 'compact' ? 'default' : 'lg'} aria-label="More actions" disabled={offline || busy} className={cn('h-11 md:h-9', size === 'compact' && 'h-8 md:h-8')}>
          More <ChevronDownIcon aria-hidden="true" data-icon="inline-end" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {park.map((o) => <MoreItem key={o.kind} option={o} parkedFrom={parkedFrom} onEvent={onEvent} />)}
        {park.length > 0 && cancel ? <DropdownMenuSeparator /> : null}
        {cancel ? <MoreItem option={cancel} parkedFrom={parkedFrom} onEvent={onEvent} icon={<BanIcon aria-hidden="true" className="size-4" />} /> : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MoreItem({ option, parkedFrom, onEvent, icon }: { option: TransitionOption; parkedFrom: Status | null; onEvent: (o: TransitionOption) => void; icon?: React.ReactNode }) {
  const refused = !option.allowed;
  return (
    <DropdownMenuItem
      aria-disabled={refused ? 'true' : undefined}
      data-event={option.kind}
      onSelect={(e) => {
        if (refused) { e.preventDefault(); return; }
        onEvent(option);
      }}
      className={cn('flex-col items-start gap-0.5 py-2', refused && 'text-beam-text-muted')}
    >
      <span className="flex items-center gap-2">{icon}{eventLabel(option.kind)}</span>
      {refused ? (
        <span className="text-xs leading-snug text-beam-text-muted">Refused: {option.reason}</span>
      ) : option.kind === 'cancel' ? (
        <span className="text-xs text-beam-text-muted">to 15 {statusLabel(15)}, terminal</span>
      ) : option.to !== undefined ? (
        <EngineLine option={option} parkedFrom={parkedFrom} id={`more-${option.kind}`} />
      ) : null}
    </DropdownMenuItem>
  );
}

/** Desktop and laptop: the cluster on the title row. */
export function ActionCluster({ status, options, parkedFrom, offline, busyEvent, onEvent, size = 'default', className }: ActionClusterProps) {
  const layout = layoutActions(status, options);
  if (layout.terminal) {
    return <p role="group" aria-label="Status actions" className={cn('text-[13px] text-beam-text-muted', className)}>15 {statusLabel(15)} is terminal. No actions are available.</p>;
  }
  return (
    <div role="group" aria-label="Status actions" className={cn('flex flex-wrap items-start gap-3', className)}>
      {layout.primary ? <ActionUnit option={layout.primary} primary parkedFrom={parkedFrom} offline={offline} busyEvent={busyEvent} onEvent={onEvent} size={size} /> : null}
      {layout.secondary.map((o) => <ActionUnit key={o.kind} option={o} primary={false} parkedFrom={parkedFrom} offline={offline} busyEvent={busyEvent} onEvent={onEvent} size={size} />)}
      <MoreMenu items={layout.more} parkedFrom={parkedFrom} offline={offline} busy={busyEvent !== null} onEvent={onEvent} size={size} />
    </div>
  );
}

/** Phone: the primary beside an Actions button; the sheet lists every unit with its engine line. */
export function PhoneActions({ status, options, parkedFrom, offline, busyEvent, onEvent, open, onOpenChange }: ActionClusterProps & { open: boolean; onOpenChange: (o: boolean) => void }) {
  const layout = layoutActions(status, options);
  if (layout.terminal) {
    return <p role="group" aria-label="Status actions" className="text-[13px] text-beam-text-muted">15 {statusLabel(15)} is terminal. No actions are available.</p>;
  }
  const all = [...(layout.primary ? [layout.primary] : []), ...layout.secondary];
  return (
    <div role="group" aria-label="Status actions" className="flex flex-col gap-1">
      <div className="flex gap-2">
        {layout.primary ? (
          <Button className="h-11 flex-[2]" data-primary="true" disabled={offline || busyEvent !== null} aria-describedby={`engine-${layout.primary.kind}`} onClick={() => onEvent(layout.primary!)}>
            {busyEvent === layout.primary.kind ? busyLabel(layout.primary.kind) : eventLabel(layout.primary.kind)}
          </Button>
        ) : null}
        <Button variant="outline" className="h-11 flex-1" onClick={() => onOpenChange(true)} disabled={offline || busyEvent !== null}>Actions</Button>
      </div>
      {layout.primary ? <EngineLine option={layout.primary} parkedFrom={parkedFrom} id={`engine-${layout.primary.kind}`} /> : null}
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="max-h-[85svh] overflow-y-auto rounded-t-[20px] shadow-beam-3">
          <div aria-hidden="true" className="mx-auto mt-2 h-1 w-10 rounded-full bg-beam-line-strong" />
          <SheetHeader className="pb-0">
            <SheetTitle>Actions</SheetTitle>
            <SheetDescription className="sr-only">Every status action with the engine's answer</SheetDescription>
          </SheetHeader>
          <ul className="flex flex-col divide-y divide-beam-line px-2 pb-4">
            {[...all, ...layout.more].map((o, i) => {
              const refused = !o.allowed;
              const isCancel = o.kind === 'cancel';
              return (
                <li key={o.kind} className={cn(isCancel && i > 0 && 'border-t-2 border-t-beam-line-strong')}>
                  <button
                    type="button"
                    data-event={o.kind}
                    aria-disabled={refused ? 'true' : undefined}
                    disabled={offline || busyEvent !== null}
                    onClick={() => { if (refused) return; onOpenChange(false); onEvent(o); }}
                    className={cn('flex min-h-14 w-full flex-col items-start justify-center gap-0.5 px-2 py-2 text-left', refused && 'text-beam-text-muted')}
                  >
                    <span className="text-base">{eventLabel(o.kind)}</span>
                    {isCancel && !refused ? <span className="text-[13px] text-beam-text-muted">to 15 {statusLabel(15)}, terminal</span> : <EngineLine option={o} parkedFrom={parkedFrom} id={`sheet-${o.kind}`} className="text-[13px]" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </SheetContent>
      </Sheet>
    </div>
  );
}
